import config from "src/config";
import { ResponseDto } from "src/dtos/common/responseDto";
import {
  PDA1120_R01Dto,
  PDA1120_R01SearchParams,
  FeederInfoDto,
  PDA1120_R01CheckRequest,
  MaterialCheckResultDto,
} from "src/containers/PDA/PDA1120_R01/PDA1120_R01Dto";
import { getData, postData, deleteData } from "src/utils/axios/serverHelper";

/**
 * PDA1120_R01 피더 자재투입 체크 API
 * 백엔드 엔드포인트: /api/v1/PDA1120_R01
 */
const materialCheckUrl = `${config.apiUrl}/PDA1120_R01`;

/**
 * 1. 공정지시 목록 조회
 * 
 * 날짜 범위 또는 공정지시번호로 공정지시 목록을 조회합니다.
 * 화면의 "공정지시" 입력값(searchText)은 백엔드 productWorkNo 조건으로 전달됩니다.
 * 
 * @param params 검색 조건 (시작일, 종료일, 공정지시번호/검색어 등)
 * @returns 작업지시 목록
 */
export const getPDA1120_R01List = async (params: PDA1120_R01SearchParams) => {
  const productWorkNo = (params.searchText?.trim() || params.productWorkNo?.trim() || undefined);
  const requestParams = {
    startDate: params.startDate || undefined,
    endDate: params.endDate || undefined,
    workplaceCd: params.workplaceCd || undefined,
    ...(productWorkNo && { productWorkNo }),
  };
  return await getData<ResponseDto<PDA1120_R01Dto[]>>(`${materialCheckUrl}/search`, {
    params: requestParams,
  });
};

/**
 * 2. 피더 목록 조회
 *
 * 공정지시번호·공정코드로 피더 목록을 조회합니다.
 * 체크 상태는 품목+피더(itemCd+feederNo) 단위로 매핑됩니다.
 *
 * @param productWorkNo 공정지시번호
 * @param workplaceCd 공정코드 (A10/B20/C30 등, 선택 시 해당 공정 피더 기준으로 실장량 표시)
 */
export const getPDA1120_R01DetailList = async (
  productWorkNo: string,
  workplaceCd?: string | null
) => {
  return await getData<ResponseDto<FeederInfoDto[]>>(`${materialCheckUrl}/feeders`, {
    params: { productWorkNo, ...(workplaceCd != null && workplaceCd !== '' && { workplaceCd }) },
  });
};

/**
 * 3. LOT 스캔 및 품목 매칭
 *
 * 스캔한 LOT 번호로 LOT 정보를 조회하고, 공정지시의 BOM과 매칭하여 검증합니다.
 * 이미 실장된 피더(checkedFeeders)는 제외하고 미실장 피더를 반환합니다.
 *
 * @param lotNo LOT 번호
 * @param productWorkNo 공정지시번호
 * @param checkedFeeders 이미 실장된 피더코드 목록 (미저장 포함, "F01,F02" 형태로 전달)
 */
export const getPDA1120_R01One = async (
  lotNo: string,
  productWorkNo: string,
  checkedFeeders: string[] = []
) => {
  return await getData<ResponseDto<MaterialCheckResultDto>>(`${materialCheckUrl}/scan-lot`, {
    params: {
      lotNo,
      productWorkNo,
      checkedFeeders: checkedFeeders.join(','),
    },
  });
};

/**
 * 4. 피더 체크 저장
 * 
 * 피더에 LOT를 투입한 체크 결과를 저장합니다.
 * 모든 피더 체크 완료 시 작업지시의 피더 상태가 자동으로 'F'(준비완료)로 업데이트됩니다.
 * 
 * @param data 체크 요청 데이터
 * @returns 처리 결과 메시지
 */
export const checkMaterial = async (data: PDA1120_R01CheckRequest) => {
  return await postData<PDA1120_R01CheckRequest, ResponseDto<string>>(
    `${materialCheckUrl}/check`,
    data
  );
};

/**
 * 5. 피더 체크 일괄 저장
 * 
 * 여러 피더 체크를 한 번에 저장합니다.
 * 
 * @param requests 체크 요청 데이터 배열
 * @returns 처리 결과 메시지
 */
export const postPDA1120_R01 = async (requests: PDA1120_R01CheckRequest[]) => {
  return await postData<PDA1120_R01CheckRequest[], ResponseDto<string>>(
    `${materialCheckUrl}/check/batch`,
    requests
  );
};

/**
 * 6. 피더 체크 취소
 * 
 * 등록된 피더 체크를 취소합니다.
 * 
 * @param productWorkNo 공정지시번호 (공정지시번호)
 * @param lotNo LOT 번호
 * @returns 처리 결과 메시지
 */
export const cancelMaterialCheck = async (productWorkNo: string, lotNo: string) => {
  return await deleteData<ResponseDto<string>>(`${materialCheckUrl}/check`, {
    params: {
      productWorkNo,  //   백엔드와 동일한 파라미터명 사용
      lotNo,
    },
  });
};
